import { useCallback, useEffect, useRef } from 'react';

import { NotificationState } from 'components/shared/Notification';
import useInvalidateQueries from 'hooks/useInvalidateQueries';
import useReferralSourceImportChannel, {
  ReferralSourceImportChannelMessage,
} from 'hooks/useReferralSourceImportChannel';
import { simpleDateAtTime } from 'lib/date';
import {
  isTerminalReferralSourceImportStatus,
  ReferralSourceImport,
  SkippedReferralSourceRow,
} from 'services/api/admin/referralSourceImports';
import { NotificationItem, useNotificationActions, useNotificationStore } from 'stores/notificationStore';

type Props = {
  referralSourceImport: ReferralSourceImport;
  onSettled: (importId: string) => void;
};

export function importSummary({ addedCount, skippedCount }: ReferralSourceImportChannelMessage) {
  const added = `${addedCount ?? 0} added`;
  if (!skippedCount) return added;

  return `${added}, ${skippedCount} skipped`;
}

function csvCell(value: string | number) {
  const text = String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

// The skipped rows as a CSV in the upload's own columns plus Reason, which the importer ignores, so
// the admin can fix the rows and upload this same file. The leading byte-order mark makes Excel read
// it as UTF-8; without it Excel on Windows garbles accented names.
export function skippedRowsCsv(rows: SkippedReferralSourceRow[]) {
  const lines = [
    ['Referral Source', 'Also Known As', 'Reason'],
    ...rows.map(({ row, name, aliases, reason }) => [name, aliases, `Row ${row}: ${reason}`]),
  ];
  return `\uFEFF${lines.map((line) => line.map(csvCell).join(',')).join('\n')}\n`;
}

// Revoked later rather than right after click: some Safari versions cancel a download whose URL is
// revoked immediately.
const REVOKE_DELAY_MS = 40_000;

function downloadSkippedRows(filename: string, rows: SkippedReferralSourceRow[]) {
  const url = URL.createObjectURL(new Blob([skippedRowsCsv(rows)], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename.replace(/\.csv$/i, '')}_skipped_rows.csv`;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), REVOKE_DELAY_MS);
}

/**
 * Turns one upload's status channel into a processing -> success/failure notification, the same way
 * the high cost medication upload does. The notification lives in the global store, so it outlives
 * this tracker.
 */
export default function ReferralSourceImportTracker({ referralSourceImport, onSettled }: Props) {
  const { addNotification, updateNotification } = useNotificationActions();
  const invalidateQueries = useInvalidateQueries();

  const settledRef = useRef(false);
  const notificationIdRef = useRef<string | null>(null);
  const filename = referralSourceImport.filename ?? 'referral source list';

  // Added before the channel effect below subscribes, so the id is set before any message arrives.
  // The ref guard stops StrictMode's double mount adding it twice.
  useEffect(() => {
    if (notificationIdRef.current) return;

    notificationIdRef.current = addNotification({
      id: referralSourceImport.id,
      state: NotificationState.PROCESSING,
      title: 'Currently processing',
      timestamp: `${filename} - ${simpleDateAtTime(new Date())}`,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMessage = useCallback(
    (data: ReferralSourceImportChannelMessage) => {
      if (settledRef.current || !notificationIdRef.current || !isTerminalReferralSourceImportStatus(data.status)) {
        return;
      }

      settledRef.current = true;
      const notificationId = notificationIdRef.current;
      // The processing notification can be closed while the import runs. The result is the only
      // place the counts and skipped rows reach the admin, so it comes back as a new notification
      // rather than updating one that is gone.
      const showResult = (result: Omit<NotificationItem, 'id'>) => {
        const stillShown = useNotificationStore.getState().notifications.some(({ id }) => id === notificationId);
        if (stillShown) updateNotification(notificationId, result);
        else addNotification({ ...result, id: notificationId });
      };

      if (data.status === 'completed') {
        invalidateQueries({ referralSources: true });
        const skippedRows = data.skippedRows ?? [];
        showResult({
          state: NotificationState.SUCCESS,
          title: `${filename} has successfully been uploaded.`,
          timestamp: importSummary(data),
          ...(skippedRows.length > 0 && {
            actionSuccessText: 'Download skipped rows',
            onActionClick: () => downloadSkippedRows(filename, skippedRows),
          }),
        });
      } else {
        showResult({
          state: NotificationState.FAILURE,
          title: `Upload failed to process for ${filename}`,
          // The importer's reason (row number and problem) so the admin can fix the file.
          timestamp: data.error ?? undefined,
        });
      }

      onSettled(referralSourceImport.id);
    },
    [filename, invalidateQueries, addNotification, updateNotification, onSettled, referralSourceImport.id]
  );

  useReferralSourceImportChannel({
    params: { referral_source_import_id: referralSourceImport.id },
    onMessageReceived: handleMessage,
  });

  return null;
}
